import { ProjectResource, usePutProjectsByProjectMutation } from '@/api/serverApi'
import { Button, TextInput, useErrorHandler } from '@cfs-fixings/shared'
import toastHelper from '@cfs-fixings/shared/src/helpers/toastHelper'
import { valibotResolver } from '@hookform/resolvers/valibot'
import { SubmitHandler, useForm } from 'react-hook-form'
import * as v from 'valibot'

const updateProjectSchema = v.object({
  name: v.string([v.minLength(1, 'Name is required')]),
})

type UpdateProjectFormInputs = v.Input<typeof updateProjectSchema>

type PropTypes = {
  project: ProjectResource,
  onCompleted?: (project: ProjectResource) => void,
  onClose?: () => void,
}

function UpdateProjectForm ({ project, onCompleted, onClose }: PropTypes) {
  const textSpec = {
    projectNameField: {
      placeholder: 'Enter name',
    },
    buttonLabels: {
      cancel: 'Cancel',
      create: 'Save Changes',
    },
    successToastMessage: {
      title: 'Project updated',
      body: 'Your project has been updated successfully.',
    },
    errorToastMessage: {
      title: 'Something went wrong',
      body: 'Please try again later.',
    },
  }

  const errorHandler = useErrorHandler()

  const { register, handleSubmit, formState: { errors }, setError, reset } = useForm<UpdateProjectFormInputs>({
    resolver: valibotResolver(updateProjectSchema),
    defaultValues: {
      name: project.name,
    },
  })

  const [update, { isLoading: isSubmitting }] = usePutProjectsByProjectMutation()

  const onSubmit: SubmitHandler<UpdateProjectFormInputs> = (data) => {
    update({
      project: project.id,
      body: data,
    })
      .unwrap()
      .then((response) => {
        onCompleted?.(response.data)
        toastHelper.success(textSpec.successToastMessage.title, textSpec.successToastMessage.body)
        reset()
      })
      .catch((error) => {
        if (error?.status === 422) {
          errorHandler(error, setError)
        } else {
          toastHelper.error(textSpec.errorToastMessage.title, error.data?.message ?? textSpec.errorToastMessage.body)
        }
      })
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col items-start w-full max-w-[615px]"
    >
      <div className="mb-[17px] w-full">
        <TextInput
          placeholder={textSpec.projectNameField.placeholder}
          error={errors?.name?.message}
          {...register('name')}
        />
      </div>

      <div className="w-full flex flex-row justify-end gap-2.5">
        <Button
          key="close"
          variant="secondary"
          onClick={onClose}
        >
          {textSpec.buttonLabels.cancel}
        </Button>
        <Button
          key="create"
          variant="primary"
          isLoading={isSubmitting}
          type="submit"
        >
          {textSpec.buttonLabels.create}
        </Button>
      </div>
    </form>
  )
}

export default UpdateProjectForm
